import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useAiReportDatasets } from '../../../../hooks/useAiReportDataset';
import { AiReportDatasetColumn, DATASET_MIN_COLUMNS } from '../../../../models/AiReportDataset';
import ColumnBuilder, { newColumnKey } from '../ColumnBuilder';

const initialColumns: AiReportDatasetColumn[] = [
    { key: newColumnKey(), label: '' },
    { key: newColumnKey(), label: '' },
];

const AddDatasetPage = () => {
    const navigate = useNavigate();
    const { addDataset } = useAiReportDatasets();
    const [name, setName] = useState('');
    const [columns, setColumns] = useState<AiReportDatasetColumn[]>(initialColumns);
    const [referredKey, setReferredKey] = useState<string>(initialColumns[0].key);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!name.trim()) {
            showErrorToast('Name is required');
            return;
        }
        const labeledColumns = columns.filter((c) => c.label.trim());
        if (labeledColumns.length < DATASET_MIN_COLUMNS) {
            showErrorToast(`Add at least ${DATASET_MIN_COLUMNS} named columns`);
            return;
        }
        if (!labeledColumns.some((c) => c.key === referredKey)) {
            showErrorToast('Pick a referred column');
            return;
        }

        setSubmitting(true);
        try {
            const created = await addDataset({ name: name.trim(), columns: labeledColumns, referredColumnKey: referredKey });
            showLoadingToast(`Creating ${created.name} ...`, 100);
            setTimeout(() => {
                navigate(`/gp/ai-report-helper/datasets/edit/${created.id}`);
                showSuccessToast(`${created.name} created &mdash; add some rows next`);
            }, 1000);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to create data set');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-4xl">
            <Link
                to="/gp/ai-report-helper/datasets"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to Data Sets
            </Link>
            <Heading className="mt-4">Create Data Set</Heading>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Name</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            A short name shown when picking this data set while converting a report.
                        </p>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Name</Subheading>
                            <div className="mt-2">
                                <Input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Customer Address Book" />
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Columns</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            2-10 columns. Pick one as the referred column &mdash; the value reports will match against.
                        </p>
                    </div>
                    <div className="max-w-2xl md:col-span-2">
                        <ColumnBuilder
                            columns={columns}
                            referredKey={referredKey}
                            onColumnsChange={setColumns}
                            onReferredKeyChange={setReferredKey}
                        />
                    </div>
                </div>
            </Card>
            <div className="my-6 flex justify-end gap-4">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={submitting}>Create</Button>
            </div>
        </form>
    );
};

export default AddDatasetPage;
